import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { FadeUp } from "@/components/site/Reveal";
import { aud } from "@/lib/api";

const RATES = [
  { id: "low", label: "Conservative", pct: 0.53 },
  { id: "mid", label: "Typical", pct: 0.7 },
  { id: "high", label: "Best case", pct: 0.87 },
];

const Result = ({ label, value, testId, accent }) => (
  <div className={`rounded-2xl p-6 ${accent ? "bg-[var(--sun)] text-[var(--ink)]" : "bg-[var(--ink)] border border-white/10"}`}>
    <p className={`font-mono text-xs tracking-[0.2em] uppercase ${accent ? "" : "text-slate-400"}`}>{label}</p>
    <p data-testid={testId} className="mt-2 font-display font-black text-5xl lg:text-6xl leading-none">{value}</p>
  </div>
);

export default function Savings() {
  const [bill, setBill] = useState(8000);
  const [meters, setMeters] = useState(1);
  const [rate, setRate] = useState("mid");

  const r = useMemo(() => {
    const pct = RATES.find((x) => x.id === rate)?.pct ?? 0.7;
    const monthly = bill * pct;
    return { monthly, yearly: monthly * 12, life: monthly * 12 * 25, rebate: meters * 70000, lease: monthly / 3, kept: (monthly * 2) / 3 };
  }, [bill, meters, rate]);

  return (
    <div data-testid="savings-page">
      <PageHero testId="savings-hero" eyebrow="Savings calculator" lines={["What could", "your wash save?"]} intro="Move the slider to your average monthly power bill. We'll show what carwash owners typically save — then we'll confirm the real numbers for your site." />
      <section className="bg-[var(--ink)] pb-24 lg:pb-32">
        <div className="mx-auto max-w-[1440px] px-5 lg:px-10 grid lg:grid-cols-12 gap-6">
          <FadeUp className="lg:col-span-5 rounded-2xl bg-[var(--surface)] border border-white/10 p-7 lg:p-10">
            <label className="font-mono text-xs tracking-[0.2em] uppercase text-slate-300" htmlFor="bill">Average monthly power bill</label>
            <p data-testid="savings-calc-bill-value" className="mt-3 font-display font-black text-6xl text-white">{aud(bill)}</p>
            <input id="bill" data-testid="savings-calc-bill-slider" type="range" min={1000} max={20000} step={250} value={bill} onChange={(e) => setBill(Number(e.target.value))} className="solar-range mt-6" />
            <div className="mt-2 flex justify-between text-xs text-slate-400"><span>$1,000</span><span>$20,000+</span></div>

            <label className="mt-10 block font-mono text-xs tracking-[0.2em] uppercase text-slate-300" htmlFor="meters">Electricity meters / sites</label>
            <div className="mt-3 flex items-center gap-3">
              <button data-testid="savings-calc-meters-dec" onClick={() => setMeters((m) => Math.max(1, m - 1))} className="h-12 w-12 rounded-full border border-white/20 text-2xl hover:border-[var(--sun)] transition-colors">−</button>
              <span id="meters" data-testid="savings-calc-meters-value" className="font-display font-black text-4xl w-14 text-center">{meters}</span>
              <button data-testid="savings-calc-meters-inc" onClick={() => setMeters((m) => Math.min(20, m + 1))} className="h-12 w-12 rounded-full border border-white/20 text-2xl hover:border-[var(--sun)] transition-colors">+</button>
            </div>

            <p className="mt-10 font-mono text-xs tracking-[0.2em] uppercase text-slate-300">Savings scenario</p>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {RATES.map((x) => (
                <button key={x.id} data-testid={`savings-calc-rate-${x.id}`} onClick={() => setRate(x.id)} className={`rounded-xl px-3 py-3 text-left transition-colors ${rate === x.id ? "bg-[var(--sun)] text-[var(--ink)]" : "bg-white/5 text-white hover:bg-white/10"}`}>
                  <span className="block font-display font-black text-2xl">{Math.round(x.pct * 100)}%</span>
                  <span className="block text-xs">{x.label}</span>
                </button>
              ))}
            </div>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-7 grid sm:grid-cols-2 gap-4 content-start">
            <Result label="Monthly savings" value={aud(r.monthly)} testId="savings-calc-result-monthly" accent />
            <Result label="Yearly savings" value={aud(r.yearly)} testId="savings-calc-result-yearly" />
            <Result label="25-year savings" value={aud(r.life)} testId="savings-calc-result-25yr" />
            <Result label="Rebates up to (from June 2026)" value={aud(r.rebate)} testId="savings-calc-result-rebate" />
            <div className="sm:col-span-2 rounded-2xl bg-[var(--navy)] border border-white/10 p-6">
              <p className="font-mono text-xs tracking-[0.2em] uppercase text-[var(--solar-light)]">With Green Operational Leasing · $0 upfront</p>
              <div className="mt-4 h-14 flex rounded-xl overflow-hidden">
                <div className="w-1/3 bg-[var(--solar)] grid place-items-center font-display font-bold text-lg" data-testid="savings-calc-lease">{aud(r.lease)}</div>
                <div className="w-2/3 bg-[var(--sun)] text-[var(--ink)] grid place-items-center font-display font-bold text-lg" data-testid="savings-calc-kept">{aud(r.kept)} kept / mo</div>
              </div>
              <p className="mt-3 text-sm text-slate-300">Indicative split based on a ~$2,000 lease from $6,000 of monthly savings. Payback is typically 2 – 2.5 years.</p>
            </div>
            <Link to={`/contact?bill=${bill}&sites=${meters}`} data-testid="savings-calc-quote-btn" className="sm:col-span-2 group flex items-center justify-between rounded-full bg-white text-[var(--ink)] px-7 py-5 font-display font-bold uppercase tracking-wider text-lg transition-colors hover:bg-[var(--sun)]">
              Confirm my real numbers <ArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <p className="sm:col-span-2 text-xs text-slate-400">Estimates only, based on the 53%–87% average savings range. Actual savings, rebates and eligibility depend on your site and are confirmed in your free assessment.</p>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
